/**
 * Leaflet 地図 (フォームモーダル内 / 一覧地図セクションで共用)。
 *
 * 主な責務:
 *   1. 編集中の放送地点を Marker (ドラッグ可) + Circle (半径) で表示する。
 *   2. 同じ routeDirection の他の放送地点は Marker で重ねて表示し、位置関係を確認できる。
 *   3. 線形データ (Point の集合) を CircleMarker で描画する。
 *      - GeoJSON が存在しない場合は描画しない。
 *      - 右上のトグルにより、アナウンス有無 / GPS 精度 (Log スケール) の
 *        2 通りの色分けを切り替えられる。
 *   4. 一覧地図モード: 編集対象が無くても、`otherPoints` のピンを地図上に並べる。
 *      ピンを `onPointClick` でクリックすると親が編集モーダルを開ける。
 *   5. 地図上クリックでマーカー位置を移動し、フォーム側に座標を反映させる
 *      (`onLocationChange` が指定された場合のみ)。
 */
import L from 'leaflet';
import { useEffect, useMemo, useState } from 'react';
import {
  Circle,
  CircleMarker,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';

import type { BroadcastPoint } from '../api/types';
import type { RouteShapePointFeature, RouteShapePointsFile } from '../data/routeShapes';

// Leaflet のデフォルトアイコンは bundler 経由だとパスが解決できないため、CDN URL に差し替える。
// (react-leaflet 5 + leaflet 1.9 の組み合わせの定石)
const DEFAULT_ICON = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const SECONDARY_ICON = L.divIcon({
  html: '<div style="width:14px;height:14px;border-radius:50%;background:#94a3b8;border:2px solid #fff;box-shadow:0 0 0 1px #94a3b8"></div>',
  className: '',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// 一覧表示時のピン (クリック可能・色を強調)
const LIST_ICON = L.divIcon({
  html: '<div style="width:18px;height:18px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 0 1px #1d4ed8;cursor:pointer"></div>',
  className: '',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

L.Marker.prototype.options.icon = DEFAULT_ICON;

/** 線形カラーモード: アナウンス有無 / GPS 精度 (Log) */
export type RouteShapeColorMode = 'announce' | 'gps_accuracy';

export interface BroadcastPointsMapProps {
  /** 編集中の地点 (緯度経度・半径)。新規作成時は緯度経度未設定でも可 */
  editingLatitude?: number;
  editingLongitude?: number;
  editingRadiusMeters?: number;
  /** 同 routeDirection の他の放送地点 (グレー / 一覧モードでは青ピン) */
  otherPoints: BroadcastPoint[];
  /**
   * 線形データ (Point Feature の集合)。
   * `null` の場合は線形を描画しない (= GeoJSON 未配信のケース)。
   */
  routeShapePoints?: RouteShapePointsFile | null;
  /** マーカーがドラッグまたはクリックで移動された時に呼ばれる (フォーム用) */
  onLocationChange?: (lat: number, lng: number) => void;
  /**
   * `otherPoints` のピンをクリックした時に呼ばれる (一覧地図モード)。
   * 指定されている場合、ピンはクリック可能な強調表示で描画され、半径円も併せて描く。
   */
  onPointClick?: (point: BroadcastPoint) => void;
  /**
   * 地図をフォーカスしたい地点。この prop が変わると `flyTo` で地点に移動する。
   * 一覧テーブルのレコードをクリックした時に地図をジャンプさせるために使う。
   */
  focusPoint?: BroadcastPoint | null;
}

export function BroadcastPointsMap(props: BroadcastPointsMapProps): React.JSX.Element {
  const {
    editingLatitude,
    editingLongitude,
    editingRadiusMeters,
    otherPoints,
    routeShapePoints,
    onLocationChange,
    onPointClick,
    focusPoint,
  } = props;

  // 線形カラーモードのトグル状態。
  // `false` (= デフォルト) でアナウンス有無、`true` で GPS 精度 (Log)。
  const [gpsMode, setGpsMode] = useState(false);
  const colorMode: RouteShapeColorMode = gpsMode ? 'gps_accuracy' : 'announce';

  // 線形 Feature が利用可能か (= GeoJSON が存在するか)
  const shapeFeatures = useMemo<RouteShapePointFeature[]>(() => {
    if (!routeShapePoints || !Array.isArray(routeShapePoints.features)) return [];
    return routeShapePoints.features.filter(
      (f) => f.geometry?.type === 'Point' && Array.isArray(f.geometry.coordinates),
    );
  }, [routeShapePoints]);

  // 初期表示中心: 編集中の座標 → 既存地点の最初 → 線形の最初の点 → 東京駅
  const initialCenter: [number, number] = useMemo(() => {
    if (editingLatitude !== undefined && editingLongitude !== undefined) {
      return [editingLatitude, editingLongitude];
    }
    if (otherPoints.length > 0) {
      const first = otherPoints.find((p) => p.latitude && p.longitude);
      if (first?.latitude && first?.longitude) {
        return [first.latitude, first.longitude];
      }
    }
    if (shapeFeatures.length > 0) {
      const [lng, lat] = shapeFeatures[0]!.geometry.coordinates;
      return [lat, lng];
    }
    return [35.681236, 139.767125];
  }, [editingLatitude, editingLongitude, otherPoints, shapeFeatures]);

  return (
    <div className="map-with-overlay">
      <MapContainer
        center={initialCenter}
        zoom={14}
        scrollWheelZoom
        style={{ width: '100%', height: '100%' }}
      >
        <TileLayer
          attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {/* 線形データ (GeoJSON が無い時は描画しない) */}
        {shapeFeatures.length > 0 ? (
          <RouteShapePointsLayer features={shapeFeatures} colorMode={colorMode} />
        ) : null}
        {/* 既存の他の地点。一覧モード (onPointClick あり) ではクリック可能な青ピン + 半径円 */}
        {otherPoints
          .filter((p) => p.latitude !== undefined && p.longitude !== undefined)
          .map((p) => {
            if (onPointClick) {
              return (
                <PointWithCircle
                  key={`${p.routeDirection}-${p.spotId}`}
                  point={p}
                  onClick={() => onPointClick(p)}
                />
              );
            }
            return (
              <Marker
                key={`${p.routeDirection}-${p.spotId}`}
                position={[p.latitude as number, p.longitude as number]}
                icon={SECONDARY_ICON}
                interactive={false}
              />
            );
          })}
        {/* 編集中の地点 (青) */}
        {editingLatitude !== undefined && editingLongitude !== undefined ? (
          <>
            <Marker
              position={[editingLatitude, editingLongitude]}
              draggable={Boolean(onLocationChange)}
              eventHandlers={
                onLocationChange
                  ? {
                      dragend: (e) => {
                        const m = e.target as L.Marker;
                        const { lat, lng } = m.getLatLng();
                        onLocationChange(lat, lng);
                      },
                    }
                  : undefined
              }
            />
            {editingRadiusMeters && editingRadiusMeters > 0 ? (
              <Circle
                center={[editingLatitude, editingLongitude]}
                radius={editingRadiusMeters}
                pathOptions={{ color: '#1d4ed8', fillColor: '#1d4ed8', fillOpacity: 0.1 }}
              />
            ) : null}
            <RecenterOnChange lat={editingLatitude} lng={editingLongitude} />
          </>
        ) : null}
        {onLocationChange ? <ClickHandler onLocationChange={onLocationChange} /> : null}
        {focusPoint ? <FlyToPoint point={focusPoint} /> : null}
        <InvalidateOnResize />
      </MapContainer>
      {/* 右上のトグル + 凡例 (線形 GeoJSON が無い時は非表示) */}
      {shapeFeatures.length > 0 ? (
        <ColorModeToggle gpsMode={gpsMode} onChange={setGpsMode} />
      ) : null}
    </div>
  );
}

/**
 * 地図コンテナのサイズ変化を検知して Leaflet の `invalidateSize` を呼ぶ。
 *
 * モーダル内でフォーム側のコンテンツ数が増えるなどして親要素の高さが
 * 動的に変わった際、Leaflet はマウント時のサイズをキャッシュしているため
 * タイルが見切れたり灰色領域が出たりする。`ResizeObserver` で監視し、
 * サイズ変化のたびに `invalidateSize` を呼んで再描画させる。
 */
function InvalidateOnResize(): null {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      map.invalidateSize();
    });
    ro.observe(container);
    return () => {
      ro.disconnect();
    };
  }, [map]);
  return null;
}

/** 一覧表示用: 1 つの BroadcastPoint をクリック可能なピン + 半径円で描画する */
function PointWithCircle({
  point,
  onClick,
}: {
  point: BroadcastPoint;
  onClick: () => void;
}): React.JSX.Element | null {
  if (point.latitude === undefined || point.longitude === undefined) return null;
  const radius = point.radius_meters && point.radius_meters > 0 ? point.radius_meters : 100;
  return (
    <>
      <Circle
        center={[point.latitude, point.longitude]}
        radius={radius}
        pathOptions={{
          color: '#94a3b8',
          weight: 1,
          fillColor: '#94a3b8',
          fillOpacity: 0.08,
        }}
      />
      <Marker
        position={[point.latitude, point.longitude]}
        icon={LIST_ICON}
        eventHandlers={{ click: onClick }}
        title={point.description ?? point.routeDirection}
      />
    </>
  );
}

/** 地図クリックで onLocationChange を呼ぶハンドラ */
function ClickHandler({
  onLocationChange,
}: {
  onLocationChange: (lat: number, lng: number) => void;
}): null {
  useMapEvents({
    click(e) {
      onLocationChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

/** 編集中の緯度経度が変わった時、地図中心を緩やかに移動する */
function RecenterOnChange({ lat, lng }: { lat: number; lng: number }): null {
  const map = useMap();
  useEffect(() => {
    map.panTo([lat, lng]);
  }, [map, lat, lng]);
  return null;
}

/**
 * `point` が変わるたびに地図をその緯度経度へ `flyTo` するコンポーネント。
 * 座標が未設定の地点は何もしない。
 */
function FlyToPoint({ point }: { point: BroadcastPoint }): null {
  const map = useMap();
  useEffect(() => {
    if (typeof point.latitude === 'number' && typeof point.longitude === 'number') {
      map.flyTo([point.latitude, point.longitude], map.getZoom(), { duration: 0.5 });
    }
  }, [map, point]);
  return null;
}

// ---------------------------------------------------------------------------
// 線形 (Point Feature コレクション) 描画関連
// ---------------------------------------------------------------------------

/** GPS 精度 (m) の Log カラーマップ閾値。値が小さいほど高精度=水色、大きいほど赤。 */
const GPS_ACCURACY_MIN_METERS = 5;
const GPS_ACCURACY_MAX_METERS = 100;

/** GPS 精度 高精度側 (水色) */
const GPS_COLOR_HIGH: RgbColor = { r: 125, g: 211, b: 252 }; // tailwind sky-300 相当
/** GPS 精度 中央 (白) */
const GPS_COLOR_MID: RgbColor = { r: 255, g: 255, b: 255 };
/** GPS 精度 低精度側 (赤) */
const GPS_COLOR_LOW: RgbColor = { r: 220, g: 38, b: 38 }; // tailwind red-600 相当

/** アナウンス区間の色 (赤系) */
const COLOR_ANNOUNCE = '#dc2626';
/** 非アナウンス区間の色 (灰系) */
const COLOR_NO_ANNOUNCE = '#94a3b8';
/** 値が無い (null) 時の色 (薄灰) */
const COLOR_UNKNOWN = '#d1d5db';

/** RGB 0-255 の単純な値オブジェクト (HSL 補間だと中央が白にならないため、RGB 線形補間で扱う) */
interface RgbColor {
  r: number;
  g: number;
  b: number;
}

/**
 * 線形データを CircleMarker の集合として描画する。
 *
 * - `colorMode === 'announce'`: `is_announcing` (true/false/null) に応じて色分け
 * - `colorMode === 'gps_accuracy'`: `gps_accuracy_avg` を Log スケールで赤→青に色分け
 *
 * 大量 (数万点) の Point があるため、各点は半径 3px の小さな CircleMarker
 * (= 画面ピクセル基準) で描画して描画コストを抑える。
 */
function RouteShapePointsLayer({
  features,
  colorMode,
}: {
  features: RouteShapePointFeature[];
  colorMode: RouteShapeColorMode;
}): React.JSX.Element {
  return (
    <>
      {features.map((f, i) => {
        const [lng, lat] = f.geometry.coordinates;
        const color = computeShapeColor(f.properties, colorMode);
        return (
          <CircleMarker
            key={`shape-${i}`}
            center={[lat, lng]}
            radius={3}
            pathOptions={{
              color,
              fillColor: color,
              fillOpacity: 0.85,
              weight: 1,
              opacity: 0.9,
            }}
            interactive={false}
          />
        );
      })}
    </>
  );
}

/** 1 点の properties から表示色を決定する */
function computeShapeColor(
  props: RouteShapePointFeature['properties'],
  mode: RouteShapeColorMode,
): string {
  if (mode === 'announce') {
    if (props.is_announcing === true) return COLOR_ANNOUNCE;
    if (props.is_announcing === false) return COLOR_NO_ANNOUNCE;
    return COLOR_UNKNOWN;
  }
  // GPS 精度モード
  const acc = props.gps_accuracy_avg;
  if (acc === null || acc === undefined || !Number.isFinite(acc) || acc <= 0) {
    return COLOR_UNKNOWN;
  }
  return gpsAccuracyToColor(acc);
}

/**
 * GPS 精度 (m) を Log スケールに正規化し、`水色 → 白 → 赤` に補間する。
 *
 * - acc が `GPS_ACCURACY_MIN_METERS` 以下 → 水色
 * - acc が `GPS_ACCURACY_MIN_METERS` と `GPS_ACCURACY_MAX_METERS` の幾何平均 → 白
 * - acc が `GPS_ACCURACY_MAX_METERS` 以上 → 赤
 *
 * HSL 補間では中央が白色にならないため、RGB 各成分を線形補間する。
 */
export function gpsAccuracyToColor(acc: number): string {
  const t = gpsAccuracyToScalar(acc);
  return rgbToHex(twoStopGradient(GPS_COLOR_HIGH, GPS_COLOR_MID, GPS_COLOR_LOW, t));
}

/**
 * GPS 精度 (m) を [0, 1] の Log スケール正規化値に変換する。
 * 0 = 高精度 (水色側) / 1 = 低精度 (赤側)。
 */
export function gpsAccuracyToScalar(acc: number): number {
  const minLog = Math.log10(GPS_ACCURACY_MIN_METERS);
  const maxLog = Math.log10(GPS_ACCURACY_MAX_METERS);
  const v = Math.log10(Math.max(acc, 1e-6));
  return Math.min(1, Math.max(0, (v - minLog) / (maxLog - minLog)));
}

/**
 * 3 色グラデーションを `t in [0, 1]` で評価して RGB を返す。
 * - `t = 0` → `lo`
 * - `t = 0.5` → `mid`
 * - `t = 1` → `hi`
 */
function twoStopGradient(lo: RgbColor, mid: RgbColor, hi: RgbColor, t: number): RgbColor {
  if (t <= 0.5) {
    const u = t / 0.5;
    return lerpRgb(lo, mid, u);
  }
  const u = (t - 0.5) / 0.5;
  return lerpRgb(mid, hi, u);
}

function lerpRgb(a: RgbColor, b: RgbColor, t: number): RgbColor {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

function rgbToHex({ r, g, b }: RgbColor): string {
  const toHex = (x: number): string => {
    const v = Math.max(0, Math.min(255, Math.round(x)));
    return v.toString(16).padStart(2, '0');
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * 地図右上のカラーモード切替コントロール + 凡例。
 *
 * Issue #130: 従来は ON/OFF の 1 つのトグルスイッチで、ラベル文言が状態に応じて
 * 「アナウンス有無」⇔「GPS精度 (Log)」と切り替わる方式だった。これは「今どちらの
 * モードか」「押すとどちらになるか」が分かりづらく使いづらかった。
 * そこで、2 つのモードを常に並べて表示し、現在選択中のモードを強調するセグメント
 * コントロール (ラジオ相当) に変更し、直感的に切り替えられるようにした。
 *
 * Leaflet の Map 内では DOM のクリックが地図イベントに伝播してしまうことが
 * あるため、`onClick` / `onMouseDown` などのイベントを stopPropagation する。
 *
 * `export` しているのは、Leaflet/JSDOM 非依存でユニットテストできるようにするため。
 */
export function ColorModeToggle({
  gpsMode,
  onChange,
}: {
  gpsMode: boolean;
  onChange: (next: boolean) => void;
}): React.JSX.Element {
  const stop: React.MouseEventHandler = (e) => {
    e.stopPropagation();
  };
  return (
    <div
      className="map-color-toggle"
      onMouseDown={stop}
      onDoubleClick={stop}
      onWheel={stop}
      onClick={stop}
    >
      <div
        className="map-color-toggle__segmented"
        role="radiogroup"
        aria-label="線形カラーモード切替"
      >
        <button
          type="button"
          role="radio"
          aria-checked={!gpsMode}
          className={`map-color-toggle__segment${!gpsMode ? ' is-active' : ''}`}
          onClick={() => onChange(false)}
        >
          アナウンス有無
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={gpsMode}
          className={`map-color-toggle__segment${gpsMode ? ' is-active' : ''}`}
          onClick={() => onChange(true)}
        >
          GPS精度 (Log)
        </button>
      </div>
      <ColorModeLegend gpsMode={gpsMode} />
    </div>
  );
}

/** カラーモードに応じた凡例 (色サンプル) */
function ColorModeLegend({ gpsMode }: { gpsMode: boolean }): React.JSX.Element {
  if (!gpsMode) {
    return (
      <ul className="map-color-toggle__legend">
        <li>
          <span className="legend-swatch" style={{ background: COLOR_ANNOUNCE }} />
          アナウンス有
        </li>
        <li>
          <span className="legend-swatch" style={{ background: COLOR_NO_ANNOUNCE }} />
          アナウンス無
        </li>
        <li>
          <span className="legend-swatch" style={{ background: COLOR_UNKNOWN }} />
          未取得
        </li>
      </ul>
    );
  }
  // GPS 精度モード: 連続グラデーション + 両端の値を併記
  // (geom-mean は閾値が幾何平均=白になるよう Log スケールの中央)
  const midAcc = Math.sqrt(GPS_ACCURACY_MIN_METERS * GPS_ACCURACY_MAX_METERS);
  const gradient =
    `linear-gradient(to right,` +
    ` ${gpsAccuracyToColor(GPS_ACCURACY_MIN_METERS)} 0%,` +
    ` ${gpsAccuracyToColor(midAcc)} 50%,` +
    ` ${gpsAccuracyToColor(GPS_ACCURACY_MAX_METERS)} 100%)`;
  return (
    <div className="map-color-toggle__gradient">
      <span className="legend-label">高精度</span>
      <span className="legend-bar" style={{ background: gradient }} />
      <span className="legend-label">低精度</span>
      <div className="legend-range">
        {GPS_ACCURACY_MIN_METERS}m – {GPS_ACCURACY_MAX_METERS}m
      </div>
    </div>
  );
}
